/**
 * Frontend wrappers for the Quran goal/plan/homework/status API (/api/quran).
 * Uses the central axios `api` instance (auth token attached by interceptors).
 * Only meaningful for Quran sub-type centers — the backend returns 403
 * otherwise, so callers should gate on `useQuranCenter().isQuran`.
 */

import api from '../services/api';

// Every caller reads `result.data`. services/api.js has already unwrapped the
// backend's { success, data } to the payload itself (a status array, a goal
// object), so `r.data` was undefined and every Hifz status column, matrix and
// tab rendered empty. Put the payload back under `data`; a body the
// interceptor left whole (data: null) keeps its own `data`.
export const toEnvelope = (r) => ({
  data: r && typeof r === 'object' && !Array.isArray(r) && 'data' in r ? r.data : r,
});

const handle = (p) => p.then(toEnvelope).catch((err) => {
  const msg = err?.response?.data?.message || err.message || 'Request failed';
  throw new Error(msg);
});

export const quranApi = {
  // Goals
  getStudentGoal(studentId) {
    return handle(api.get(`/quran/goals/student/${studentId}`));
  },
  // payload: { studentId, groupId?, surahNumbers[], direction, source }
  saveGoal(payload) {
    return handle(api.post('/quran/goals', payload));
  },

  // Memorized baseline
  getMemorized(studentId) {
    return handle(api.get(`/quran/memorized/${studentId}`));
  },
  // payload: { ranges: [{fromSurah,fromAyah,toSurah,toAyah}] }
  setMemorized(studentId, ranges) {
    return handle(api.put(`/quran/memorized/${studentId}`, { ranges }));
  },
  // Merged "Record Memorization": append ranges to one student or a whole group,
  // optionally logging a recitation. payload:
  //   { studentId?|groupId?, ranges:[...], recite?: { rating?, mistakeCount?, passed?, notes? } }
  recordMemorization(payload) {
    return handle(api.post('/quran/memorized/bulk', payload));
  },

  // Plans
  listPlans({ groupId, studentId } = {}) {
    const qs = new URLSearchParams();
    if (groupId) qs.set('groupId', groupId);
    if (studentId) qs.set('studentId', studentId);
    return handle(api.get(`/quran/plans?${qs.toString()}`));
  },
  savePlan(payload) {
    return handle(api.post('/quran/plans', payload));
  },
  updatePlan(id, patch) {
    return handle(api.put(`/quran/plans/${id}`, patch));
  },

  // Homework
  listHomework({ studentId, groupId, status } = {}) {
    const qs = new URLSearchParams();
    if (studentId) qs.set('studentId', studentId);
    if (groupId) qs.set('groupId', groupId);
    if (status) qs.set('status', status);
    return handle(api.get(`/quran/homework?${qs.toString()}`));
  },
  // payload: { studentId|groupId, type, range, dueLessonDate, planItemSeq?, note? }
  assignHomework(payload) {
    return handle(api.post('/quran/homework', payload));
  },
  updateHomework(id, patch) {
    return handle(api.patch(`/quran/homework/${id}`, patch));
  },
  removeHomework(id) {
    return handle(api.delete(`/quran/homework/${id}`));
  },

  // Status rollups
  groupStatus(groupId) {
    return handle(api.get(`/quran/status?groupId=${groupId}`));
  },
  studentStatus(studentId) {
    return handle(api.get(`/quran/status/student/${studentId}`));
  },
};

export default quranApi;
